import React, { useCallback, useEffect, useMemo, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import {
  defaultStockUsageChargeable,
  isLowStock,
  isMissingStockSchemaError,
  partRowFromStockItem,
  stockItemMeta,
  stockItemSearchText,
  stockItemTitle,
  stockLineCost,
  stockLineSell,
  stockMoneyLabel,
  stockQuantity,
  stockQuantityLabel,
  stockText,
  stockUnitLabel,
  stockUsagePayloadFromItem,
  stockUsageStatusLabel,
  stockUsageStatusTone,
} from '../lib/stock'

function stockUsageRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, value => {
    const random = Math.floor(Math.random() * 16)
    const digit = value === 'x' ? random : (random & 0x3) | 0x8
    return digit.toString(16)
  })
}

function blankForm(chargedToCustomer = true) {
  return {
    stock_item_id: '',
    quantity: '1',
    charged_to_customer: chargedToCustomer,
    customer_description: '',
    internal_note: '',
    add_to_parts: true,
    idempotency_key: stockUsageRequestId(),
  }
}

function sourceTitle(sourceType) {
  if (sourceType === 'suspension_parts') return 'Use from stock'
  if (sourceType === 'general_additional_work') return 'Linked stock / materials'
  return 'Job stock usage'
}

function sourceDescription(sourceType) {
  if (sourceType === 'suspension_parts') return 'Adding an item records it as used immediately. Chargeable stock counts as billable parts/materials without changing kit recommendations.'
  if (sourceType === 'general_additional_work') return 'Link planned workshop stock to this additional-work request. Billing stays controlled by the approved request amount.'
  return 'Adding an item records it as used immediately. Chargeable stock counts as billable stock/materials on this job.'
}

function sourceHint(sourceType) {
  if (sourceType === 'suspension_parts') return 'Stock is decremented once when added. Optional visible Parts Used lines are not counted twice.'
  if (sourceType === 'general_additional_work') return 'This is a planned/link record only. Confirm it when used; billing remains with the additional-work request.'
  return 'Stock is decremented once when added. Non-chargeable items reduce inventory without increasing the customer total.'
}

function usageName(row = {}) {
  return stockText(row.stock_name_snapshot) || 'Stock item'
}

export default function JobStockUsagePanel({
  jobId,
  workshopId,
  mode = 'edit',
  sourceType = 'manual',
  additionalWorkRequestId = null,
  title,
  description,
  compact = false,
  vatInherited = false,
  onApplyToParts,
  onChanged,
}) {
  const { profile } = useAuth()
  const editable = mode === 'edit'
  const isOwner = profile?.role === 'owner' && profile?.workshop_id === workshopId
  const directStockDefault = defaultStockUsageChargeable(sourceType, additionalWorkRequestId)
  const linkedAdditionalWork = !directStockDefault
  const [items, setItems] = useState([])
  const [rows, setRows] = useState([])
  const [form, setForm] = useState(() => blankForm(directStockDefault))
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState('')
  const [missingSchema, setMissingSchema] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [vatPrice, setVatPrice] = useState(null)

  const selectedItem = useMemo(() => items.find(item => item.id === form.stock_item_id) || null, [items, form.stock_item_id])
  const priceScope = JSON.stringify([jobId, workshopId, form.stock_item_id, form.quantity, form.charged_to_customer, additionalWorkRequestId])
  const needsPrice = process.env.REACT_APP_VAT_BUILD3 === 'true' && vatInherited && selectedItem && form.charged_to_customer && !additionalWorkRequestId
  useEffect(() => {
    let current = true
    setVatPrice(null)
    if (needsPrice) supabase.rpc('workshop_vat_stock_prices', {
      p_job_id: jobId, p_stock_item_id: form.stock_item_id, p_quantity: stockQuantity(form.quantity),
    }).then(({ data, error: priceError }) => {
      if (current) setVatPrice({ scope: priceScope, value: data, error: priceError?.message })
    }).catch(priceError => { if (current) setVatPrice({ scope: priceScope, error: priceError.message }) })
    return () => { current = false }
  }, [priceScope, needsPrice, jobId, form.stock_item_id, form.quantity])
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase()
    return items
      .filter(item => item.active !== false && !item.archived_at)
      .filter(item => !term || stockItemSearchText(item).includes(term))
      .sort((a, b) => {
        if (isLowStock(a) !== isLowStock(b)) return isLowStock(a) ? -1 : 1
        const categorySort = stockText(a.category).localeCompare(stockText(b.category))
        if (categorySort !== 0) return categorySort
        return stockText(a.name).localeCompare(stockText(b.name))
      })
  }, [items, search])

  const load = useCallback(async () => {
    if (!jobId || !workshopId) {
      setItems([])
      setRows([])
      return
    }
    setLoading(true)
    setError('')
    try {
      const itemQuery = supabase
        .from('stock_items')
        .select('*')
        .eq('workshop_id', workshopId)
        .order('category')
        .order('name')

      let usageQuery = supabase
        .from('job_stock_usage')
        .select('*')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .order('created_at', { ascending: false })

      usageQuery = additionalWorkRequestId
        ? usageQuery.eq('additional_work_request_id', additionalWorkRequestId)
        : usageQuery.is('additional_work_request_id', null)

      const [itemResult, usageResult] = await Promise.all([itemQuery, usageQuery])
      if (itemResult.error) throw itemResult.error
      if (usageResult.error) throw usageResult.error
      setItems(itemResult.data || [])
      setRows(usageResult.data || [])
      setMissingSchema(false)
    } catch (err) {
      if (isMissingStockSchemaError(err)) {
        setMissingSchema(true)
        setItems([])
        setRows([])
      } else {
        setError(err.message || 'Could not load stock usage.')
      }
    } finally {
      setLoading(false)
    }
  }, [jobId, workshopId, additionalWorkRequestId])

  useEffect(() => {
    load()
  }, [load])

  function setField(key, value) {
    setForm(current => ({ ...current, [key]: value }))
  }

  function chooseStockItem(id) {
    const item = items.find(row => row.id === id)
    setForm(current => ({
      ...current,
      stock_item_id: id,
      customer_description: item ? stockText(item.description) || stockText(item.name) : current.customer_description,
    }))
  }

  async function recordUsage() {
    if (!jobId || !workshopId) {
      setError('Save the job before recording stock usage.')
      return
    }
    if (!selectedItem) {
      setError('Choose a stock item first.')
      return
    }

    const quantity = stockQuantity(form.quantity) || 1
    const snapshot = stockUsagePayloadFromItem(selectedItem, quantity)
    const linkedAdditionalWork = Boolean(additionalWorkRequestId)
    if (needsPrice && (vatPrice?.scope !== priceScope || vatPrice?.error)) {
      setError(vatPrice?.error || 'Wait for the current customer charge before recording usage.')
      return
    }

    setSaving('record')
    setError('')
    setNotice('')
    try {
      const { data: recorded, error: recordError } = await supabase.rpc('record_job_stock_usage', {
        p_job_id: jobId,
        p_stock_item_id: selectedItem.id,
        p_quantity: quantity,
        p_charged_to_customer: linkedAdditionalWork ? false : Boolean(form.charged_to_customer),
        p_customer_description: stockText(form.customer_description) || snapshot.customer_description,
        p_internal_note: form.internal_note || '',
        p_source_type: linkedAdditionalWork ? 'general_additional_work' : sourceType,
        p_additional_work_request_id: additionalWorkRequestId || null,
        p_idempotency_key: form.idempotency_key,
        ...(vatPrice?.scope === priceScope && vatPrice.value ? { p_expected_prices: vatPrice.value } : {}),
      })

      if (recordError) throw recordError
      if (recorded?.created && !vatInherited && !linkedAdditionalWork && onApplyToParts && form.add_to_parts) {
        onApplyToParts(partRowFromStockItem(selectedItem, quantity))
      }
      setForm(blankForm(directStockDefault))
      setShowForm(false)
      await load()
      setNotice(linkedAdditionalWork
        ? 'Stock item linked to this additional work.'
        : 'Stock item recorded as used and quantity updated.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not save stock usage.')
    } finally {
      setSaving('')
    }
  }

  async function confirmUsage(row) {
    setSaving(`confirm-${row.id}`)
    setError('')
    setNotice('')
    try {
      const { error: confirmError } = await supabase.rpc('confirm_job_stock_usage', { p_usage_id: row.id })
      if (confirmError) throw confirmError
      await load()
      setNotice('Stock usage confirmed and quantity updated.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not confirm stock usage.')
    } finally {
      setSaving('')
    }
  }

  async function voidUsage(row) {
    const reason = typeof window !== 'undefined'
      ? window.prompt('Reason for voiding this stock usage?')
      : 'Voided from job card'
    if (reason === null) return
    setSaving(`void-${row.id}`)
    setError('')
    setNotice('')
    try {
      const { error: voidError } = await supabase.rpc('void_job_stock_usage', {
        p_usage_id: row.id,
        p_reason: stockText(reason) || 'Voided from job card',
      })
      if (voidError) throw voidError
      await load()
      setNotice('Stock usage voided.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not void stock usage.')
    } finally {
      setSaving('')
    }
  }

  if (!jobId) {
    return (
      <div className="job-stock-panel compact">
        <div className="fs12 text-muted">Save the job before recording stock usage.</div>
      </div>
    )
  }

  if (missingSchema) {
    return (
      <div className="job-stock-panel compact">
        <div className="fs12 text-muted">Stock schema v85 has not been applied yet.</div>
      </div>
    )
  }

  const panelTitle = title || sourceTitle(sourceType)
  const panelDescription = description || sourceDescription(sourceType)
  const unresolvedLegacyRows = rows.filter(row => (
    row.status === 'draft'
    && row.charged_to_customer
    && !row.additional_work_request_id
    && row.source_type !== 'general_additional_work'
  ))

  return (
    <div className={`job-stock-panel${compact ? ' compact' : ''}`}>
      <div className="section-head compact" style={{ marginBottom: 10 }}>
        <div>
          <div className="job-modal-callout-title">{panelTitle}</div>
          <div className="text-muted fs12">{panelDescription}</div>
        </div>
        {editable && (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => {
              if (!showForm) setForm(blankForm(directStockDefault))
              setShowForm(open => !open)
            }}
          >
            {showForm ? 'Close' : '+ Stock Item'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
      {notice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{notice}</div>}
      {unresolvedLegacyRows.length > 0 && (
        <div className="alert alert-error" style={{ marginBottom: 10 }}>
          {unresolvedLegacyRows.length} unresolved legacy chargeable stock item{unresolvedLegacyRows.length === 1 ? '' : 's'} must be confirmed or voided before payment, invoice or receipt actions.
        </div>
      )}
      {loading && <div className="fs12 text-muted">Loading stock usage...</div>}

      {showForm && editable && (
        <div className="job-stock-draft">
          <div className="grid-3">
            <div className="field">
              <label>Find Stock</label>
              <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, code, supplier..."
              />
            </div>
            <div className="field">
              <label>Stock Item</label>
              <select value={form.stock_item_id} onChange={e => chooseStockItem(e.target.value)}>
                <option value="">Choose item...</option>
                {filteredItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {stockItemTitle(item)} · {stockQuantityLabel(item.quantity_on_hand, item.unit_type)}{isLowStock(item) ? ' · low' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Quantity</label>
              <input type="number" min="0.001" step="0.001" value={form.quantity} onChange={e => setField('quantity', e.target.value)} />
            </div>
          </div>

          {selectedItem && (
            <div className="stock-picked-row">
              <div>
                <strong>{stockItemTitle(selectedItem)}</strong>
                <small>{stockItemMeta(selectedItem) || 'No extra stock metadata'}</small>
              </div>
              <div>
                <StatusPill tone={isLowStock(selectedItem) ? 'orange' : 'muted'}>
                  {isLowStock(selectedItem) ? 'Low stock' : stockQuantityLabel(selectedItem.quantity_on_hand, selectedItem.unit_type)}
                </StatusPill>
                <span className="job-status-chip job-status-muted">{needsPrice
                  ? vatPrice?.scope === priceScope && !vatPrice.error
                    ? vatPrice.value ? `Customer total ${stockMoneyLabel(vatPrice.value.tax.gross_minor / 100)}` : `Sell ${stockMoneyLabel(selectedItem.sell_price)}`
                    : vatPrice?.error || 'Checking customer total…'
                  : `Sell ${stockMoneyLabel(selectedItem.sell_price)}`}</span>
              </div>
            </div>
          )}

          <div className="grid-2">
            <div className="field">
              <label>Customer Description</label>
              <input
                value={form.customer_description}
                onChange={e => setField('customer_description', e.target.value)}
                placeholder="Customer-safe description if this line appears elsewhere."
              />
            </div>
            <div className="field">
              <label>Internal Note</label>
              <input
                value={form.internal_note}
                onChange={e => setField('internal_note', e.target.value)}
                placeholder="Bin, batch, reason, or fitment note."
              />
            </div>
          </div>

          <div className="action-row" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {!linkedAdditionalWork && (
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.charged_to_customer}
                    onChange={e => setField('charged_to_customer', e.target.checked)}
                  /> Charged to customer
                </label>
              )}
              {!linkedAdditionalWork && !vatInherited && onApplyToParts && (
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.add_to_parts}
                    onChange={e => setField('add_to_parts', e.target.checked)}
                  /> Add visible Parts Used line
                </label>
              )}
            </div>
            <button className="btn btn-primary btn-sm" type="button" onClick={recordUsage} disabled={Boolean(saving)}>
              {saving === 'record' ? 'Saving...' : linkedAdditionalWork ? 'Link Stock Item' : 'Add Used Stock'}
            </button>
          </div>
          <div className="field-hint" style={{ marginTop: 8 }}>
            {sourceHint(sourceType)}
          </div>
        </div>
      )}

      {!loading && rows.length === 0 && !showForm && (
        <div className="muted-empty">No stock usage recorded.</div>
      )}

      {rows.length > 0 && (
        <div className="job-stock-usage-list">
          {rows.map(row => {
            const statusTone = stockUsageStatusTone(row.status)
            const isDraft = row.status === 'draft'
            const isConfirmed = row.status === 'confirmed_used'
            const isLinkedAdditionalWork = row.source_type === 'general_additional_work' || Boolean(row.additional_work_request_id)
            const unresolvedLegacy = isDraft && row.charged_to_customer && !isLinkedAdditionalWork
            return (
              <div key={row.id} className="job-stock-usage-row">
                <div className="job-stock-usage-main">
                  <strong>{usageName(row)}</strong>
                  <small>
                    {stockQuantityLabel(row.quantity, row.unit_type)}
                    {row.stock_internal_code_snapshot ? ` · ${row.stock_internal_code_snapshot}` : ''}
                    {row.stock_supplier_snapshot ? ` · ${row.stock_supplier_snapshot}` : ''}
                  </small>
                  {row.customer_description && <small>{row.customer_description}</small>}
                  {row.internal_note && editable && <small>Internal: {row.internal_note}</small>}
                </div>
                <div className="job-stock-usage-meta">
                  <StatusPill tone={unresolvedLegacy ? 'orange' : statusTone}>
                    {isLinkedAdditionalWork && isDraft ? 'Linked / planned' : unresolvedLegacy ? 'Legacy action required' : stockUsageStatusLabel(row.status)}
                  </StatusPill>
                  <span className="job-status-chip job-status-muted">Cost {stockMoneyLabel(stockLineCost(row))}</span>
                  <span className="job-status-chip job-status-muted">Sell {stockMoneyLabel(stockLineSell(row))}</span>
                  {row.charged_to_customer && !isLinkedAdditionalWork && <span className="job-status-chip job-status-blue">Chargeable</span>}
                  {isLinkedAdditionalWork && <span className="job-status-chip job-status-muted">Billed by Additional Work</span>}
                  {editable && isDraft && (
                    <button
                      className="btn btn-green btn-sm"
                      type="button"
                      onClick={() => confirmUsage(row)}
                      disabled={saving === `confirm-${row.id}`}
                    >
                      {saving === `confirm-${row.id}` ? 'Confirming...' : unresolvedLegacy ? 'Include & Confirm Used' : 'Confirm Used'}
                    </button>
                  )}
                  {editable && isOwner && (isDraft || isConfirmed) && (
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={() => voidUsage(row)}
                      disabled={saving === `void-${row.id}`}
                    >
                      {saving === `void-${row.id}` ? 'Voiding...' : 'Void'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
